import { useIsMobile } from "../../../utils";
import { FC, useState } from "react";
import { Drawer, DrawerContent, DrawerTrigger } from "../Drawer";
import { Button } from "../Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../Command";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { PlanningEvent } from "./utils";
import { getPlanningEventHours } from "./PlanningCards";
import { isNotNull } from "@ac-expo/utils";
import dayjs from "dayjs";

type ActivitySearchBar = {
  slots: PlanningEvent[];
  openActivityDetails: (id: string) => void;
};
export const ActivitySearchBar: FC<ActivitySearchBar> = ({
  slots,
  openActivityDetails,
}) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const onSelect = (searchable: string) => {
    const id = getActivityIdFromSearchableActivity(searchable);
    openActivityDetails(id);
    setOpen(false);
  };

  const activities = slots
    .map((s) =>
      s.activityId
        ? {
            id: s.activityId,
            name: s.title,
            day: dayjs(s.start).format("DD/MM"),
            hours: s.allDay ? "Toute la journée" : getPlanningEventHours(s),
          }
        : null
    )
    .filter(isNotNull);

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <Button
            variant="outline"
            className="h-full w-1/2 cursor-text rounded-lg border-gray-300 bg-gray-200 p-3.5 text-sm text-gray-900 hover:bg-gray-200"
          >
            <div className="flex w-full items-center justify-start gap-2">
              <MagnifyingGlass
                name="magnifyingGlass"
                alt="magnifying_glass"
                width={20}
                height={20}
              />
              <input
                type="search"
                className="w-full bg-gray-200 focus-visible:outline-none"
                placeholder="Rechercher"
              />
            </div>
          </Button>
        </DrawerTrigger>
        <DrawerContent>
          <div className="mt-4 border-t">
            <ActivityList activities={activities} selectActivity={onSelect} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-[41px] w-1/2 cursor-text rounded-lg border-gray-300 bg-white p-3.5 text-sm text-gray-900 hover:bg-white"
        >
          <div className="flex w-full items-center justify-start gap-2">
            <MagnifyingGlass
              name="magnifyingGlass"
              alt="magnifying_glass"
              width={20}
              height={20}
            />
            <input
              type="search"
              className="w-full bg-white focus-visible:outline-none"
              placeholder="Rechercher"
            />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <ActivityList activities={activities} selectActivity={onSelect} />
      </PopoverContent>
    </Popover>
  );
};

type Activity = {
  id: string;
  name: string;
  day: string;
  hours: string;
};
function ActivityList({
  activities,
  selectActivity,
}: {
  activities: Activity[];
  selectActivity: (id: string) => void;
}) {
  return (
    <Command>
      <CommandInput placeholder="Rechercher" />
      <CommandList>
        <CommandEmpty>Pas de résultats.</CommandEmpty>
        <CommandGroup>
          {activities.map((activity) => (
            <CommandItem
              key={activity.id}
              value={getSearchableActivity(activity)}
              onSelect={selectActivity}
            >
              <div>
                <p>{activity.name}</p>
                <p className="text-xs text-gray-400">
                  {activity.day} · {activity.hours}
                </p>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

const getSearchableActivity = (activity: Activity) => {
  return `${activity.id}_${activity.name}`;
};
const getActivityIdFromSearchableActivity = (searchableActivity: string) => {
  const [id, _] = searchableActivity.split("_");
  return id;
};
